import type {
  ChamberMemberProMembershipModelPaymentResponsibilityEnum,
  ChamberMemberProPaymentScheduleEnum,
  CmpMemberMembershipInput,
  CmpPaymentChoiceTypeEnum,
} from "../../../graphql/types";

export type MemberMembershipPresentationOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type MemberMembershipPresentationInput = {
  configuredOptions: MemberMembershipPresentationOption[];
  isSettingsLoading: boolean;
  membershipTypeId?: string | null;
  membershipTypeName?: string | null;
};

type MemberMembershipPresentation = {
  isPersistedMembershipUnavailable: boolean;
  options: MemberMembershipPresentationOption[];
  placeholder: string;
};

type PersistedMembershipForProfileUpdate = {
  membershipTypeId: string;
  paymentSchedule: ChamberMemberProPaymentScheduleEnum;
  paymentResponsibility?:
    | ChamberMemberProMembershipModelPaymentResponsibilityEnum
    | null;
  thirdPartyPayerName?: string | null;
  thirdPartyPaymentNotes?: string | null;
  fullTimeEmployees: number;
  partTimeEmployees: number;
  selectedAdditionalCostIds?: string[] | null;
  membershipDiscount?: {
    enabled: boolean;
    code?: string | null;
    description?: string | null;
    amountInCents: number;
  } | null;
  paymentChoice: {
    type: CmpPaymentChoiceTypeEnum;
    manualPaymentMethodId?: string | null;
    autoRenew: boolean;
  };
};

export const getMembershipTypeDisplayLabel = (
  membershipTypeName?: string | null
): string => {
  const normalizedName = (membershipTypeName || "").trim();
  return normalizedName || "No membership assigned";
};

/**
 * Keeps the persisted membership visible while Chamber Set Up loads and after
 * a saved type has been disabled or removed. A truly missing membership gets
 * explicit empty-state copy instead of looking like a loading/select glitch.
 */
export const getMemberMembershipPresentation = ({
  configuredOptions,
  isSettingsLoading,
  membershipTypeId,
  membershipTypeName,
}: MemberMembershipPresentationInput): MemberMembershipPresentation => {
  const options = configuredOptions.map((option) => ({ ...option }));
  const persistedMembershipTypeId = (membershipTypeId || "").trim();

  if (!persistedMembershipTypeId) {
    return {
      isPersistedMembershipUnavailable: false,
      options,
      placeholder: "No membership assigned",
    };
  }

  if (
    options.some((option) => option.value === persistedMembershipTypeId)
  ) {
    return {
      isPersistedMembershipUnavailable: false,
      options,
      placeholder: "Select Membership Type",
    };
  }

  const persistedLabel =
    (membershipTypeName || "").trim() || "Current membership";

  options.unshift({
    value: persistedMembershipTypeId,
    label: `${persistedLabel} ${
      isSettingsLoading ? "(Current)" : "(Current - unavailable)"
    }`,
    disabled: true,
  });

  return {
    isPersistedMembershipUnavailable: !isSettingsLoading,
    options,
    placeholder: "Select Membership Type",
  };
};

/**
 * Profile updates are not membership-change operations. Preserve every saved
 * billing field exactly and only apply the ordinary email-consent edit exposed
 * on the profile form. This prevents unavailable card/monthly settings from
 * silently rewriting or blocking an unrelated member-profile save.
 */
export const buildProfileUpdateMembershipInput = ({
  emailCommunicationConsentConfirmed,
  persistedMembership,
}: {
  emailCommunicationConsentConfirmed: boolean;
  persistedMembership?: PersistedMembershipForProfileUpdate | null;
}): CmpMemberMembershipInput | undefined => {
  if (!persistedMembership) {
    return undefined;
  }

  return {
    membershipTypeId: persistedMembership.membershipTypeId,
    paymentSchedule: persistedMembership.paymentSchedule,
    paymentResponsibility: persistedMembership.paymentResponsibility,
    thirdPartyPayerName: persistedMembership.thirdPartyPayerName,
    thirdPartyPaymentNotes: persistedMembership.thirdPartyPaymentNotes,
    fullTimeEmployees: persistedMembership.fullTimeEmployees,
    partTimeEmployees: persistedMembership.partTimeEmployees,
    selectedAdditionalCostIds: [
      ...(persistedMembership.selectedAdditionalCostIds || []),
    ],
    membershipDiscount: persistedMembership.membershipDiscount
      ? {
          enabled: persistedMembership.membershipDiscount.enabled,
          code: persistedMembership.membershipDiscount.code,
          description: persistedMembership.membershipDiscount.description,
          amountInCents:
            persistedMembership.membershipDiscount.amountInCents,
        }
      : undefined,
    paymentChoice: {
      type: persistedMembership.paymentChoice.type,
      manualPaymentMethodId:
        persistedMembership.paymentChoice.manualPaymentMethodId,
      autoRenew: persistedMembership.paymentChoice.autoRenew,
      emailCommunicationConsentConfirmed,
    },
  };
};
